import React, { useState, useEffect } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { vehicleService } from '../../api/services/vehicleService';
import { vcdbService } from '../../api/services/vcdbService';
import EditApplicationModal from './EditApplicationModal';
import AddApplicationModal from './AddApplicationModal';
import AddCargoApplicationModal from './AddCargoApplicationModal';
import EditCargoApplicationModal from './EditCargoApplicationModal';
import AddDashApplicationModal from './AddDashApplicationModal';
import EditDashApplicationModal from './EditDashApplicationModal';
import {
  Vehicle,
  VehiclesResponse,
  VehicleLookupData,
  Make,
  Model,
  SubModel,
  BodyStyle,
  DriveType,
  TransmissionType,
} from '../../../../shared/types';
import toast from 'react-hot-toast';
import { Loader2, Plus, FileDown, Printer } from 'lucide-react';

type ViewMode = 'index' | 'edit' | 'export';

const EMPTY_ADD_FORM = {
  makeId: '',
  modelId: '',
  subModelId: '',
  fromYear: '',
  toYear: '',
  bodyStyleId: '',
  notes: '',
};

const Vehicles: React.FC = () => {
  // ── Index view state ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('index');
  const [vehiclesData, setVehiclesData] = useState<VehiclesResponse | null>(null);
  const [lookupData, setLookupData] = useState<VehicleLookupData | null>(null);
  const [selectedMakeId, setSelectedMakeId] = useState<number | undefined>(undefined);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(undefined);
  const [filterModels, setFilterModels] = useState<Model[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [partSearchQuery, setPartSearchQuery] = useState('');

  // ── Add / Edit form state ────────────────────────────────────────────────────
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [vehicleApplications, setVehicleApplications] = useState<any[]>([]);
  const [addPartNumber, setAddPartNumber] = useState('');
  const [addPartLoading, setAddPartLoading] = useState(false);
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const [editAppPartId, setEditAppPartId] = useState<number | null>(null);
  const [editAppPartNumber, setEditAppPartNumber] = useState('');
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [vcdbLoading, setVcdbLoading] = useState(false);

  // Cascading VCdb lookup data (loaded on demand)
  const [vcdbMakes, setVcdbMakes] = useState<Make[]>([]);
  const [vcdbModels, setVcdbModels] = useState<Model[]>([]);
  const [vcdbSubModels, setVcdbSubModels] = useState<SubModel[]>([]);
  const [vcdbYears, setVcdbYears] = useState<number[]>([]);
  const [vcdbBodyStyles, setVcdbBodyStyles] = useState<BodyStyle[]>([]);
  const [vcdbDriveTypes, setVcdbDriveTypes] = useState<DriveType[]>([]);
  const [vcdbTransmissionTypes, setVcdbTransmissionTypes] = useState<TransmissionType[]>([]);

  // Multi-select checkbox selections
  const [selectedDriveTypeIds, setSelectedDriveTypeIds] = useState<number[]>([]);
  const [selectedTransmissionTypeIds, setSelectedTransmissionTypeIds] = useState<number[]>([]);

  // ── Part type filter ─────────────────────────────────────────────────────────
  const [partType, setPartType] = useState<'Seat Covers' | 'Cargo Liner' | 'Dash Cover'>('Seat Covers');
  const [showEmptyVehicles, setShowEmptyVehicles] = useState(false);
  const [cargoApplications, setCargoApplications] = useState<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]>([]);
  const [showAddCargoModal, setShowAddCargoModal] = useState(false);
  const [editCargoPartId, setEditCargoPartId] = useState<number | null>(null);
  const [editCargoPartNumber, setEditCargoPartNumber] = useState('');
  const [dashApplications, setDashApplications] = useState<{ Id: number; PartId: number; PartNumber: string; Extras?: string | null }[]>([]);
  const [showAddDashModal, setShowAddDashModal] = useState(false);
  const [editDashPartId, setEditDashPartId] = useState<number | null>(null);
  const [editDashPartNumber, setEditDashPartNumber] = useState('');

  // ── Export view state ────────────────────────────────────────────────────────
  const [exportMakeIds, setExportMakeIds] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (view !== 'edit') loadData(partType);
  }, [partType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (overridePartType?: string) => {
    const pt = overridePartType ?? partType;
    try {
      setLoading(true);
      if (pt === 'Cargo Liner') {
        const [vehicles, lookup] = await Promise.all([
          vehicleService.getCargoVehicles(),
          vehicleService.getCargoVehicleLookupData(),
        ]);
        setVehiclesData(vehicles);
        setLookupData(lookup as any);
      } else if (pt === 'Dash Cover') {
        const [vehicles, lookup] = await Promise.all([
          vehicleService.getDashVehicles(),
          vehicleService.getDashVehicleLookupData(),
        ]);
        setVehiclesData(vehicles);
        setLookupData(lookup as any);
      } else {
        const [vehicles, lookup] = await Promise.all([
          vehicleService.getVehicles(undefined, undefined, 1, pt, showEmptyVehicles),
          vehicleService.getLookupData(),
        ]);
        setVehiclesData(vehicles);
        setLookupData(lookup);
      }
      setCurrentPage(1);
      setSelectedMakeId(undefined);
      setSelectedModelId(undefined);
      setFilterModels([]);
      setExportMakeIds([]);
      if (overridePartType) setShowEmptyVehicles(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  // ── Index view handlers ──────────────────────────────────────────────────────
  const handleMakeFilterChange = async (makeId: string) => {
    const id = makeId === '' ? undefined : parseInt(makeId);
    setSelectedMakeId(id);
    setSelectedModelId(undefined);
    setFilterModels([]);
    setCurrentPage(1);
    try {
      setLoading(true);
      const vehicles = partType === 'Cargo Liner'
        ? await vehicleService.getCargoVehicles(id, undefined, 1)
        : partType === 'Dash Cover'
        ? await vehicleService.getDashVehicles(id, undefined, 1)
        : await vehicleService.getVehicles(id, undefined, 1, partType, showEmptyVehicles);
      setVehiclesData(vehicles);
      if (vehicles.availableModels) {
        setFilterModels(vehicles.availableModels.map(m => ({ Id: m.Id, Name: m.Name, MakeId: id! })));
      }
    } catch (error) {
      console.error('Error filtering vehicles:', error);
      toast.error('Failed to filter vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleModelFilterChange = async (modelId: string) => {
    const id = modelId === '' ? undefined : parseInt(modelId);
    setSelectedModelId(id);
    setCurrentPage(1);
    try {
      setLoading(true);
      const vehicles = partType === 'Cargo Liner'
        ? await vehicleService.getCargoVehicles(selectedMakeId, id, 1)
        : partType === 'Dash Cover'
        ? await vehicleService.getDashVehicles(selectedMakeId, id, 1)
        : await vehicleService.getVehicles(selectedMakeId, id, 1, partType, showEmptyVehicles);
      setVehiclesData(vehicles);
    } catch (error) {
      console.error('Error filtering vehicles by model:', error);
      toast.error('Failed to filter vehicles');
    } finally {
      setLoading(false);
    }
  };

  const goToPage = async (page: number) => {
    setCurrentPage(page);
    try {
      setLoading(true);
      const vehicles = partType === 'Cargo Liner'
        ? await vehicleService.getCargoVehicles(selectedMakeId, selectedModelId, page)
        : partType === 'Dash Cover'
        ? await vehicleService.getDashVehicles(selectedMakeId, selectedModelId, page)
        : await vehicleService.getVehicles(selectedMakeId, selectedModelId, page, partType, showEmptyVehicles);
      setVehiclesData(vehicles);
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const getPageRange = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
    if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  const handleShowEmptyChange = async (checked: boolean) => {
    setShowEmptyVehicles(checked);
    setCurrentPage(1);
    try {
      setLoading(true);
      const vehicles = await vehicleService.getVehicles(selectedMakeId, selectedModelId, 1, partType, checked);
      setVehiclesData(vehicles);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handlePartSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partSearchQuery) {
      toast.error('Please enter a part number');
      return;
    }
    try {
      setLoading(true);
      const part = await vehicleService.getPartApplicationByPartNumber(partSearchQuery);
      if (!part) {
        toast.error(`Part number "${partSearchQuery}" not found`);
        return;
      }
      setEditAppPartId(part.Id);
      setEditAppPartNumber(part.PartNumber);
    } catch (error: any) {
      console.error('Error searching part:', error);
      toast.error('Failed to find part application');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    if (!window.confirm(`Are you sure you want to delete this vehicle: ${vehicle.Make} ${vehicle.Model}?`)) return;
    try {
      if (partType === 'Cargo Liner') {
        await vehicleService.deleteCargoVehicle(vehicle.Id!);
      } else if (partType === 'Dash Cover') {
        await vehicleService.deleteDashVehicle(vehicle.Id!);
      } else {
        await vehicleService.deleteVehicle(vehicle.Id!);
      }
      toast.success('Vehicle deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  // ── Add Vehicle form helpers ─────────────────────────────────────────────────
  const resetFormCascade = (level: 'all' | 'model' | 'submodel' | 'year' | 'yearDependent') => {
    if (level === 'all' || level === 'model') {
      setVcdbModels([]);
      setAddForm(prev => ({ ...prev, modelId: '', subModelId: '', fromYear: '', toYear: '', bodyStyleId: '' }));
    }
    if (level === 'all' || level === 'model' || level === 'submodel') {
      setVcdbSubModels([]);
      setVcdbYears([]);
      if (level !== 'model') setAddForm(prev => ({ ...prev, subModelId: '', fromYear: '', toYear: '', bodyStyleId: '' }));
    }
    if (level === 'all' || level === 'model' || level === 'submodel' || level === 'year') {
      setVcdbYears([]);
      setAddForm(prev => ({ ...prev, fromYear: '', toYear: '', bodyStyleId: '' }));
    }
    setVcdbBodyStyles([]);
    setVcdbDriveTypes([]);
    setVcdbTransmissionTypes([]);
    setSelectedDriveTypeIds([]);
    setSelectedTransmissionTypeIds([]);
  };

  const loadYearDependentData = async (
    makeId: string, modelId: string, subModelId: string,
    fromYear: string, toYear: string
  ) => {
    if (!makeId || !modelId || !fromYear || !toYear) return;
    const from = parseInt(fromYear);
    const to = parseInt(toYear);
    if (from > to) return;

    const subId = subModelId ? parseInt(subModelId) : 0;
    try {
      setVcdbLoading(true);
      const [bodyStyles, driveTypes, transmissionTypes] = await Promise.all([
        vcdbService.getBodyStyles(parseInt(makeId), parseInt(modelId), subId, from, to),
        vcdbService.getDriveTypes(parseInt(makeId), parseInt(modelId), subId, from, to),
        vcdbService.getTransmissionTypes(parseInt(makeId), parseInt(modelId), subId, from, to),
      ]);
      setVcdbBodyStyles(bodyStyles);
      setVcdbDriveTypes(driveTypes);
      setVcdbTransmissionTypes(transmissionTypes);
      setSelectedDriveTypeIds([]);
      setSelectedTransmissionTypeIds([]);
      // Preserve bodyStyleId if still valid for new year range, otherwise clear it
      setAddForm(prev => {
        const stillValid = bodyStyles.some((bs: any) => String(bs.Id) === prev.bodyStyleId);
        return { ...prev, bodyStyleId: stillValid ? prev.bodyStyleId : '' };
      });
    } catch {
      toast.error('Failed to load vehicle configuration data');
    } finally {
      setVcdbLoading(false);
    }
  };

  // ── Open Add Vehicle ─────────────────────────────────────────────────────────
  const openAddVehicle = async () => {
    setEditingVehicle(null);
    setVehicleApplications([]);
    setAddForm(EMPTY_ADD_FORM);
    setVcdbModels([]);
    setVcdbSubModels([]);
    setVcdbYears([]);
    setVcdbBodyStyles([]);
    setVcdbDriveTypes([]);
    setVcdbTransmissionTypes([]);
    setSelectedDriveTypeIds([]);
    setSelectedTransmissionTypeIds([]);
    setView('edit');

    try {
      setVcdbLoading(true);
      const makes = await vcdbService.getMakes();
      setVcdbMakes(Array.isArray(makes) ? makes : []);
    } catch {
      toast.error('Failed to load makes');
    } finally {
      setVcdbLoading(false);
    }
  };

  // ── Cascade change handlers ──────────────────────────────────────────────────
  const handleAddMakeChange = async (makeId: string) => {
    setAddForm(prev => ({ ...prev, makeId, modelId: '', subModelId: '', fromYear: '', toYear: '', bodyStyleId: '' }));
    setVcdbModels([]);
    setVcdbSubModels([]);
    setVcdbYears([]);
    setVcdbBodyStyles([]);
    setVcdbDriveTypes([]);
    setVcdbTransmissionTypes([]);
    setSelectedDriveTypeIds([]);
    setSelectedTransmissionTypeIds([]);

    if (!makeId) return;
    try {
      setVcdbLoading(true);
      const models = await vcdbService.getModels(parseInt(makeId));
      setVcdbModels(models);
    } catch {
      toast.error('Failed to load models');
    } finally {
      setVcdbLoading(false);
    }
  };

  const handleAddModelChange = async (modelId: string) => {
    const currentMakeId = addForm.makeId;
    setAddForm(prev => ({ ...prev, modelId, subModelId: '', fromYear: '', toYear: '', bodyStyleId: '' }));
    setVcdbSubModels([]);
    setVcdbYears([]);
    setVcdbBodyStyles([]);
    setVcdbDriveTypes([]);
    setVcdbTransmissionTypes([]);
    setSelectedDriveTypeIds([]);
    setSelectedTransmissionTypeIds([]);

    if (!modelId || !currentMakeId) return;
    try {
      setVcdbLoading(true);
      const [subModels, years] = await Promise.all([
        vcdbService.getSubModels(parseInt(currentMakeId), parseInt(modelId)),
        vcdbService.getYears(parseInt(currentMakeId), parseInt(modelId), 0),
      ]);
      setVcdbSubModels(subModels);
      setVcdbYears(years);
    } catch {
      toast.error('Failed to load vehicle data');
    } finally {
      setVcdbLoading(false);
    }
  };

  const handleAddSubModelChange = async (subModelId: string) => {
    const { makeId, modelId } = addForm;
    setAddForm(prev => ({ ...prev, subModelId, fromYear: '', toYear: '', bodyStyleId: '' }));
    setVcdbYears([]);
    setVcdbBodyStyles([]);
    setVcdbDriveTypes([]);
    setVcdbTransmissionTypes([]);
    setSelectedDriveTypeIds([]);
    setSelectedTransmissionTypeIds([]);

    if (!makeId || !modelId) return;
    try {
      setVcdbLoading(true);
      const subId = subModelId ? parseInt(subModelId) : 0;
      const years = await vcdbService.getYears(parseInt(makeId), parseInt(modelId), subId);
      setVcdbYears(years);
    } catch {
      toast.error('Failed to load years');
    } finally {
      setVcdbLoading(false);
    }
  };

  const handleFromYearChange = async (fromYear: string) => {
    setAddForm(prev => ({ ...prev, fromYear }));
    const { makeId, modelId, subModelId, toYear } = addForm;
    if (fromYear && toYear) {
      await loadYearDependentData(makeId, modelId, subModelId, fromYear, toYear);
    }
  };

  const handleToYearChange = async (toYear: string) => {
    setAddForm(prev => ({ ...prev, toYear }));
    const { makeId, modelId, subModelId, fromYear } = addForm;
    if (fromYear && toYear) {
      await loadYearDependentData(makeId, modelId, subModelId, fromYear, toYear);
    }
  };

  const toggleDriveType = (id: number) => {
    setSelectedDriveTypeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleTransmissionType = (id: number) => {
    setSelectedTransmissionTypeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAddVehicleSubmit = async () => {
    if (!addForm.makeId || !addForm.modelId || !addForm.fromYear || !addForm.toYear) {
      toast.error('Please fill in Make, Model, and Year range');
      return;
    }
    try {
      if (partType === 'Cargo Liner') {
        await vehicleService.createCargoVehicle({
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
        });
      } else if (partType === 'Dash Cover') {
        await vehicleService.createDashVehicle({
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
        });
      } else {
        await vehicleService.createVehicle({
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
          DriveTypeIds: selectedDriveTypeIds,
          TransmissionTypeIds: selectedTransmissionTypeIds,
          Make: '', Model: '', DriveTypes: [], TransmissionTypes: [], BodyConfigs: [],
        });
      }
      toast.success('Vehicle created successfully');
      setView('index');
      loadData();
    } catch (error) {
      console.error('Error creating vehicle:', error);
      toast.error('Failed to create vehicle');
    }
  };

  // ── Cargo application handlers ───────────────────────────────────────────────
  const handleRemoveCargoApplication = async (cargoId: number) => {
    if (!editingVehicle) return;
    if (!window.confirm('Remove this cargo part?')) return;
    try {
      await vehicleService.removeCargoApplication(editingVehicle.Id!, cargoId);
      setCargoApplications(prev => prev.filter(a => a.Id !== cargoId));
    } catch {
      // toast shown by apiClient interceptor
    }
  };

  const handleRemoveDashApplication = async (dashId: number) => {
    if (!editingVehicle) return;
    if (!window.confirm('Remove this dash cover part?')) return;
    try {
      await vehicleService.removeDashApplication(editingVehicle.Id!, dashId);
      setDashApplications(prev => prev.filter(a => a.Id !== dashId));
    } catch {
      // toast shown by apiClient interceptor
    }
  };

  // ── Open Edit Vehicle ─────────────────────────────────────────────────────────
  const openEditVehicle = async (vehicle: Vehicle) => {
    setView('edit');
    setVehicleApplications([]);
    setCargoApplications([]);
    setDashApplications([]);

    if (partType === 'Dash Cover') {
      try {
        setVcdbLoading(true);
        const fullVehicle = await vehicleService.getDashVehicleById(vehicle.Id!);
        setEditingVehicle(fullVehicle);

        const subId = fullVehicle.SubModelId || 0;
        setAddForm({
          makeId: String(fullVehicle.MakeId),
          modelId: String(fullVehicle.ModelId),
          subModelId: fullVehicle.SubModelId ? String(fullVehicle.SubModelId) : '',
          fromYear: String(fullVehicle.FromYear),
          toYear: String(fullVehicle.ToYear),
          bodyStyleId: fullVehicle.BodyStyleId ? String(fullVehicle.BodyStyleId) : '',
          notes: fullVehicle.Notes || '',
        });

        const settled = await Promise.allSettled([
          vcdbMakes.length > 0 ? Promise.resolve(vcdbMakes) : vcdbService.getMakes(),
          vcdbService.getModels(fullVehicle.MakeId),
          vcdbService.getSubModels(fullVehicle.MakeId, fullVehicle.ModelId),
          vcdbService.getYears(fullVehicle.MakeId, fullVehicle.ModelId, subId),
          vcdbService.getBodyStyles(fullVehicle.MakeId, fullVehicle.ModelId, subId, fullVehicle.FromYear, fullVehicle.ToYear),
          vehicleService.getDashApplications(fullVehicle.Id!),
        ]);

        const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
          r.status === 'fulfilled' ? r.value : fallback;

        setVcdbMakes(Array.isArray(val(settled[0], [])) ? val(settled[0], []) as Make[] : []);
        setVcdbModels(Array.isArray(val(settled[1], [])) ? val(settled[1], []) as Model[] : []);
        setVcdbSubModels(Array.isArray(val(settled[2], [])) ? val(settled[2], []) as SubModel[] : []);
        setVcdbYears(Array.isArray(val(settled[3], [])) ? val(settled[3], []) as number[] : []);
        setVcdbBodyStyles(Array.isArray(val(settled[4], [])) ? val(settled[4], []) as BodyStyle[] : []);
        setDashApplications(Array.isArray(val(settled[5], [])) ? val(settled[5], []) as { Id: number; PartId: number; PartNumber: string; Extras?: string | null }[] : []);
        setSelectedDriveTypeIds([]);
        setSelectedTransmissionTypeIds([]);
        setVcdbDriveTypes([]);
        setVcdbTransmissionTypes([]);
      } catch (error) {
        console.error('Error loading dash cover vehicle for editing:', error);
        toast.error('Failed to load vehicle data');
      } finally {
        setVcdbLoading(false);
      }
      return;
    }

    if (partType === 'Cargo Liner') {
      try {
        setVcdbLoading(true);
        const fullVehicle = await vehicleService.getCargoVehicleById(vehicle.Id!);
        setEditingVehicle(fullVehicle);

        const subId = fullVehicle.SubModelId || 0;
        setAddForm({
          makeId: String(fullVehicle.MakeId),
          modelId: String(fullVehicle.ModelId),
          subModelId: fullVehicle.SubModelId ? String(fullVehicle.SubModelId) : '',
          fromYear: String(fullVehicle.FromYear),
          toYear: String(fullVehicle.ToYear),
          bodyStyleId: fullVehicle.BodyStyleId ? String(fullVehicle.BodyStyleId) : '',
          notes: fullVehicle.Notes || '',
        });

        const settled = await Promise.allSettled([
          vcdbMakes.length > 0 ? Promise.resolve(vcdbMakes) : vcdbService.getMakes(),
          vcdbService.getModels(fullVehicle.MakeId),
          vcdbService.getSubModels(fullVehicle.MakeId, fullVehicle.ModelId),
          vcdbService.getYears(fullVehicle.MakeId, fullVehicle.ModelId, subId),
          vcdbService.getBodyStyles(fullVehicle.MakeId, fullVehicle.ModelId, subId, fullVehicle.FromYear, fullVehicle.ToYear),
          vehicleService.getCargoApplications(fullVehicle.Id!),
        ]);

        const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
          r.status === 'fulfilled' ? r.value : fallback;

        setVcdbMakes(Array.isArray(val(settled[0], [])) ? val(settled[0], []) as Make[] : []);
        setVcdbModels(Array.isArray(val(settled[1], [])) ? val(settled[1], []) as Model[] : []);
        setVcdbSubModels(Array.isArray(val(settled[2], [])) ? val(settled[2], []) as SubModel[] : []);
        setVcdbYears(Array.isArray(val(settled[3], [])) ? val(settled[3], []) as number[] : []);
        setVcdbBodyStyles(Array.isArray(val(settled[4], [])) ? val(settled[4], []) as BodyStyle[] : []);
        setCargoApplications(Array.isArray(val(settled[5], [])) ? val(settled[5], []) as { Id: number; PartId: number; PartNumber: string; Extras?: string | null }[] : []);
        setSelectedDriveTypeIds([]);
        setSelectedTransmissionTypeIds([]);
        setVcdbDriveTypes([]);
        setVcdbTransmissionTypes([]);
      } catch (error) {
        console.error('Error loading cargo vehicle for editing:', error);
        toast.error('Failed to load vehicle data');
      } finally {
        setVcdbLoading(false);
      }
      return;
    }

    try {
      setVcdbLoading(true);
      const fullVehicle = await vehicleService.getVehicleById(vehicle.Id!);
      setEditingVehicle(fullVehicle);

      const subId = fullVehicle.SubModelId || 0;
      const from = fullVehicle.FromYear;
      const to = fullVehicle.ToYear;

      setAddForm({
        makeId: String(fullVehicle.MakeId),
        modelId: String(fullVehicle.ModelId),
        subModelId: fullVehicle.SubModelId ? String(fullVehicle.SubModelId) : '',
        fromYear: String(from),
        toYear: String(to),
        bodyStyleId: fullVehicle.BodyStyleId ? String(fullVehicle.BodyStyleId) : '',
        notes: fullVehicle.Notes || '',
      });
      setSelectedDriveTypeIds(fullVehicle.DriveTypeIds || []);
      setSelectedTransmissionTypeIds(fullVehicle.TransmissionTypeIds || []);

      const settled = await Promise.allSettled([
        vcdbMakes.length > 0 ? Promise.resolve(vcdbMakes) : vcdbService.getMakes(),
        vcdbService.getModels(fullVehicle.MakeId),
        vcdbService.getSubModels(fullVehicle.MakeId, fullVehicle.ModelId),
        vcdbService.getYears(fullVehicle.MakeId, fullVehicle.ModelId, subId),
        vcdbService.getBodyStyles(fullVehicle.MakeId, fullVehicle.ModelId, subId, from, to),
        vcdbService.getDriveTypes(fullVehicle.MakeId, fullVehicle.ModelId, subId, from, to),
        vcdbService.getTransmissionTypes(fullVehicle.MakeId, fullVehicle.ModelId, subId, from, to),
        vehicleService.getApplications(fullVehicle.Id!),
        vehicleService.getCargoApplications(fullVehicle.Id!),
      ]);

      const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
        r.status === 'fulfilled' ? r.value : fallback;

      setVcdbMakes(Array.isArray(val(settled[0], [])) ? val(settled[0], []) as Make[] : []);
      setVcdbModels(Array.isArray(val(settled[1], [])) ? val(settled[1], []) as Model[] : []);
      setVcdbSubModels(Array.isArray(val(settled[2], [])) ? val(settled[2], []) as SubModel[] : []);
      setVcdbYears(Array.isArray(val(settled[3], [])) ? val(settled[3], []) as number[] : []);
      setVcdbBodyStyles(Array.isArray(val(settled[4], [])) ? val(settled[4], []) as BodyStyle[] : []);
      setVcdbDriveTypes(Array.isArray(val(settled[5], [])) ? val(settled[5], []) as DriveType[] : []);
      setVcdbTransmissionTypes(Array.isArray(val(settled[6], [])) ? val(settled[6], []) as TransmissionType[] : []);
      setVehicleApplications(Array.isArray(val(settled[7], [])) ? val(settled[7], []) as any[] : []);
      setCargoApplications(Array.isArray(val(settled[8], [])) ? val(settled[8], []) as { Id: number; PartId: number; PartNumber: string; Extras?: string | null }[] : []);
    } catch (error) {
      console.error('Error loading vehicle for editing:', error);
      toast.error('Failed to load vehicle data');
    } finally {
      setVcdbLoading(false);
    }
  };

  const handleEditVehicleSubmit = async () => {
    if (!editingVehicle) return;
    try {
      if (partType === 'Cargo Liner') {
        await vehicleService.updateCargoVehicle(editingVehicle.Id!, {
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
        });
      } else if (partType === 'Dash Cover') {
        await vehicleService.updateDashVehicle(editingVehicle.Id!, {
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
        });
      } else {
        await vehicleService.updateVehicle(editingVehicle.Id!, {
          ...editingVehicle,
          MakeId: parseInt(addForm.makeId),
          ModelId: parseInt(addForm.modelId),
          SubModelId: addForm.subModelId ? parseInt(addForm.subModelId) : 0,
          FromYear: parseInt(addForm.fromYear),
          ToYear: parseInt(addForm.toYear),
          BodyStyleId: addForm.bodyStyleId ? parseInt(addForm.bodyStyleId) : undefined,
          Notes: addForm.notes,
          DriveTypeIds: selectedDriveTypeIds,
          TransmissionTypeIds: selectedTransmissionTypeIds,
        });
      }
      toast.success('Vehicle updated successfully');
      setEditingVehicle(null);
      setView('index');
      loadData();
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error('Failed to update vehicle');
    }
  };

  // ── Application link handlers ────────────────────────────────────────────────
  const handleAddApplication = async () => {
    if (!editingVehicle || !addPartNumber.trim()) return;
    try {
      setAddPartLoading(true);
      await vehicleService.addApplication(editingVehicle.Id!, addPartNumber.trim());
      const apps = await vehicleService.getApplications(editingVehicle.Id!);
      setVehicleApplications(Array.isArray(apps) ? apps : []);
      setAddPartNumber('');
    } catch {
      // toast shown by apiClient interceptor
    } finally {
      setAddPartLoading(false);
    }
  };

  const handleRemoveApplication = async (partId: number) => {
    if (!editingVehicle) return;
    if (!window.confirm('Remove this part application link?')) return;
    try {
      await vehicleService.removeApplication(editingVehicle.Id!, partId);
      setVehicleApplications(prev => prev.filter(a => a.PartId !== partId));
    } catch {
      // toast shown by apiClient interceptor
    }
  };

  // ── Export handlers ──────────────────────────────────────────────────────────
  const toggleExportMake = (makeId: number) => {
    setExportMakeIds(prev =>
      prev.includes(makeId) ? prev.filter(id => id !== makeId) : [...prev, makeId]
    );
  };

  const handleExportToExcel = async () => {
    if (exportMakeIds.length === 0) { toast.error('Please select at least one make'); return; }
    try {
      setExporting(true);
      const blob = partType === 'Cargo Liner'
        ? await vehicleService.exportCargoToExcel(exportMakeIds)
        : partType === 'Dash Cover'
        ? await vehicleService.exportDashToExcel(exportMakeIds)
        : await vehicleService.exportToExcel(exportMakeIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = partType === 'Cargo Liner' ? 'cargo_liner_export.xls' : partType === 'Dash Cover' ? 'dash_cover_export.xls' : 'vehicles_export.xls';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handlePrintPartList = () => {
    if (exportMakeIds.length === 0) { toast.error('Please select at least one make'); return; }
    const params = new URLSearchParams();
    exportMakeIds.forEach(id => params.append('id', String(id)));
    params.set('printPartList', 'true');
    params.set('partType', partType);
    const win = window.open(`/vehicle-applications/vehicles/print?${params.toString()}`, '_blank');
    if (!win) { toast.error('Pop-up blocked — please allow pop-ups'); }
  };

  // ── Loading spinner ──────────────────────────────────────────────────────────
  if (loading && !vehiclesData) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      </MainLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">

          {/* Card header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Vehicles</h2>
              <div className="flex gap-4 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Part Type</label>
                  <select
                    value={partType}
                    onChange={(e) => setPartType(e.target.value as 'Seat Covers' | 'Cargo Liner' | 'Dash Cover')}
                    className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="Seat Covers">Seat Covers</option>
                    <option value="Cargo Liner">Cargo Liner</option>
                    <option value="Dash Cover">Dash Cover</option>
                  </select>
                </div>
                <span className="text-gray-400">|</span>
                <button
                  onClick={() => setView('export')}
                  className={`text-sm ${view === 'export' ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
                >
                  Export
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={() => setView('index')}
                  className={`text-sm ${view === 'index' ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
                >
                  Manage
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">

            {/* ── Index View ─────────────────────────────────────────────────── */}
            {view === 'index' && (
              <div>
                <div className="mb-4 flex gap-4 items-end">
                  <div>
                    <button
                      onClick={openAddVehicle}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Vehicle
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Show Make
                    </label>
                    <select
                      value={selectedMakeId || ''}
                      onChange={(e) => handleMakeFilterChange(e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">[All]</option>
                      {lookupData?.makes?.map((make) => (
                        <option key={make.Id} value={make.Id}>{make.Name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedMakeId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Show Model
                      </label>
                      <select
                        value={selectedModelId || ''}
                        onChange={(e) => handleModelFilterChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">[All]</option>
                        {filterModels.map((model) => (
                          <option key={model.Id} value={model.Id}>{model.Name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {partType === 'Seat Covers' && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer self-end pb-2">
                      <input
                        type="checkbox"
                        checked={showEmptyVehicles}
                        onChange={(e) => handleShowEmptyChange(e.target.checked)}
                        className="w-4 h-4 accent-blue-600"
                      />
                      Show vehicles without parts
                    </label>
                  )}

                  <form onSubmit={handlePartSearch} className="flex gap-2 items-end ml-auto">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Search Part
                      </label>
                      <input
                        type="text"
                        maxLength={5}
                        value={partSearchQuery}
                        onChange={(e) => setPartSearchQuery(e.target.value.toUpperCase())}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Part #"
                      />
                    </div>
                    <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
                      Go!
                    </button>
                  </form>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {['Make','Model','From Year','To Year','Body Type',
                          ...(partType !== 'Cargo Liner' && partType !== 'Dash Cover' ? ['Drive Type','Transmission'] : []),
                          'SubModel',''].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {loading ? (
                        <tr>
                          <td colSpan={partType === 'Cargo Liner' || partType === 'Dash Cover' ? 7 : 9} className="px-4 py-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400 mx-auto" />
                          </td>
                        </tr>
                      ) : vehiclesData && vehiclesData.vehicles.length > 0 ? (
                        vehiclesData.vehicles.map((vehicle) => (
                          <tr key={vehicle.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{vehicle.Make}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{vehicle.Model}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{vehicle.FromYear}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{vehicle.ToYear}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                              {vehicle.BodyConfigs?.map((bc, idx) => <div key={idx}>{bc}</div>)}
                            </td>
                            {partType !== 'Cargo Liner' && partType !== 'Dash Cover' && (
                              <>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                  {vehicle.DriveTypes?.map((dt, idx) => <div key={idx}>{dt}</div>)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                  {vehicle.TransmissionTypes?.map((tt, idx) => <div key={idx}>{tt}</div>)}
                                </td>
                              </>
                            )}
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{vehicle.SubModel}</td>
                            <td className="px-4 py-3 text-sm">
                              <button onClick={() => openEditVehicle(vehicle)} className="text-blue-600 dark:text-blue-400 hover:underline mr-2">Edit</button>
                              |
                              <button onClick={() => handleDeleteVehicle(vehicle)} className="text-red-600 dark:text-red-400 hover:underline ml-2">Delete</button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={partType === 'Cargo Liner' || partType === 'Dash Cover' ? 7 : 9} className="px-4 py-8 text-center text-gray-600 dark:text-gray-400">
                            No vehicles found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {vehiclesData && vehiclesData.totalPages && vehiclesData.totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Showing{' '}
                        <span className="font-medium">{(currentPage - 1) * (vehiclesData.pageSize ?? 50) + 1}</span>
                        {' – '}
                        <span className="font-medium">{Math.min(currentPage * (vehiclesData.pageSize ?? 50), vehiclesData.count)}</span>
                        {' of '}
                        <span className="font-medium">{vehiclesData.count}</span> records
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage === 1 || loading}
                          className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          ‹
                        </button>
                        {getPageRange(currentPage, vehiclesData.totalPages).map((p, idx) =>
                          p === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => goToPage(p as number)}
                              disabled={loading}
                              className={`px-3 py-1 text-sm rounded border ${
                                p === currentPage
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              } disabled:opacity-40 disabled:cursor-not-allowed`}
                            >
                              {p}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage === vehiclesData.totalPages || loading}
                          className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Export View ────────────────────────────────────────────────── */}
            {view === 'export' && (
              <div>
                {/* Check All / Uncheck All */}
                <div className="flex justify-end mb-3 gap-3">
                  <button
                    onClick={() => setExportMakeIds((lookupData?.makes ?? []).map(m => m.Id))}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    [Check All]
                  </button>
                  <button
                    onClick={() => setExportMakeIds([])}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    [Uncheck All]
                  </button>
                </div>

                {/* Make checkboxes — 7 columns */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-2 mb-6">
                  {(lookupData?.makes ?? []).map(make => (
                    <label key={make.Id} className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exportMakeIds.includes(make.Id)}
                        onChange={() => toggleExportMake(make.Id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600"
                      />
                      {make.Name}
                    </label>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleExportToExcel}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                    Export to Excel
                  </button>
                  <button
                    onClick={handlePrintPartList}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <Printer className="w-4 h-4" />
                    Print Part List
                  </button>
                </div>
              </div>
            )}

            {/* ── Add Vehicle Form ───────────────────────────────────────────── */}
            {view === 'edit' && (
              <div>
                {/* Title row */}
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingVehicle
                      ? `Edit Vehicle — ${editingVehicle.Make} ${editingVehicle.Model} (${editingVehicle.FromYear}–${editingVehicle.ToYear})`
                      : 'Add Vehicle'}
                  </h3>
                  <button
                    onClick={() => { setEditingVehicle(null); setView('index'); }}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    Back
                  </button>
                </div>

                <hr className="border-gray-200 dark:border-gray-700 mb-5" />

                {/* VCdb loading indicator */}
                {vcdbLoading && (
                  <div className="flex items-center gap-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </div>
                )}

                <div className="space-y-5">

                  {/* Row 1: Make · Model · Sub Model · Year */}
                  <div className="flex flex-wrap gap-5 items-end">

                    {/* Make */}
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Make</label>
                      <select
                        value={addForm.makeId}
                        onChange={(e) => handleAddMakeChange(e.target.value)}
                        disabled={vcdbLoading && vcdbMakes.length === 0}
                        className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-36 disabled:opacity-50"
                      >
                        <option value=""></option>
                        {(vcdbMakes || []).map((m) => (
                          <option key={m.Id} value={m.Id}>{m.Name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Model */}
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Model</label>
                      <select
                        value={addForm.modelId}
                        onChange={(e) => handleAddModelChange(e.target.value)}
                        disabled={!addForm.makeId || vcdbLoading}
                        className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-44 disabled:opacity-50"
                      >
                        <option value=""></option>
                        {(vcdbModels || []).map((m) => (
                          <option key={m.Id} value={m.Id}>{m.Name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Sub Model */}
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Sub Model</label>
                      <select
                        value={addForm.subModelId}
                        onChange={(e) => handleAddSubModelChange(e.target.value)}
                        disabled={!addForm.modelId || vcdbLoading}
                        className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-44 disabled:opacity-50"
                      >
                        <option value=""></option>
                        {(vcdbSubModels || []).map((sm) => (
                          <option key={sm.Id} value={sm.Id}>{sm.Name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Year range */}
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Year</label>
                      <div className="flex items-center gap-1">
                        <select
                          value={addForm.fromYear}
                          onChange={(e) => handleFromYearChange(e.target.value)}
                          disabled={vcdbYears.length === 0 || vcdbLoading}
                          className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-24 disabled:opacity-50"
                        >
                          <option value=""></option>
                          {vcdbYears.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                        <span className="text-gray-500 dark:text-gray-400 text-sm">-</span>
                        <select
                          value={addForm.toYear}
                          onChange={(e) => handleToYearChange(e.target.value)}
                          disabled={vcdbYears.length === 0 || vcdbLoading}
                          className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-24 disabled:opacity-50"
                        >
                          <option value=""></option>
                          {vcdbYears.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Body Style (appears after year range is chosen) */}
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Body Style</label>
                    <select
                      value={addForm.bodyStyleId}
                      onChange={(e) => setAddForm(prev => ({ ...prev, bodyStyleId: e.target.value }))}
                      disabled={vcdbBodyStyles.length === 0}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-56 disabled:opacity-50"
                    >
                      <option value=""></option>
                      {vcdbBodyStyles.map((bs) => (
                        <option key={bs.Id} value={bs.Id}>
                          {bs.BodyTypeName}{bs.NumDoors ? ` / ${bs.NumDoors} doors` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Row 3: Drive Type checkboxes (seat covers only) */}
                  {partType !== 'Cargo Liner' && partType !== 'Dash Cover' && vcdbDriveTypes.length > 0 && (
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Drive Type</label>
                      <div className="flex flex-wrap gap-4">
                        {vcdbDriveTypes.map((dt) => (
                          <label key={dt.Id} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedDriveTypeIds.includes(dt.Id)}
                              onChange={() => toggleDriveType(dt.Id)}
                              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600"
                            />
                            {dt.Name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Row 4: Transmission Type checkboxes (seat covers only) */}
                  {partType !== 'Cargo Liner' && partType !== 'Dash Cover' && vcdbTransmissionTypes.length > 0 && (
                    <div>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Transmission Type</label>
                      <div className="flex flex-wrap gap-4">
                        {vcdbTransmissionTypes.map((tt) => (
                          <label key={tt.Id} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTransmissionTypeIds.includes(tt.Id)}
                              onChange={() => toggleTransmissionType(tt.Id)}
                              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600"
                            />
                            {tt.Name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Row 5: Notes */}
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                    <textarea
                      value={addForm.notes}
                      onChange={(e) => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
                      rows={5}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm resize w-80"
                    />
                  </div>
                </div>

                {/* Current Applications (edit mode only) */}
                {editingVehicle && partType === 'Seat Covers' && (
                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">Seat Cover Applications</h4>
                    </div>
                    <hr className="border-gray-200 dark:border-gray-700 mb-3" />

                    <div className="flex justify-end mb-3">
                      <button
                        onClick={() => setShowAddAppModal(true)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        + Add Part
                      </button>
                    </div>

                    {vehicleApplications.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No linked parts found.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {vehicleApplications.map((app, idx) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2 w-28">
                                  <button
                                    onClick={() => { setEditAppPartId(app.PartId); setEditAppPartNumber(app.PartNumber ?? String(app.PartId)); }}
                                    className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                                  >
                                    {app.PartNumber ?? app.PartId}
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {[app.SeatTypeName, app.Extras].filter(Boolean).join(' ')}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 w-12 text-center">
                                  {app.SeatRowCode ?? ''}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 w-12 text-center">
                                  {app.PriceCode ?? ''}
                                </td>
                                <td className="px-3 py-2 text-right w-16">
                                  <button
                                    onClick={() => handleRemoveApplication(app.PartId)}
                                    className="text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Cargo Liner Applications (edit mode only) */}
                {editingVehicle && partType === 'Cargo Liner' && (
                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">Cargo Liner Applications</h4>
                    </div>
                    <hr className="border-gray-200 dark:border-gray-700 mb-3" />

                    <div className="flex justify-end mb-3">
                      <button
                        onClick={() => setShowAddCargoModal(true)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        + Add Part
                      </button>
                    </div>

                    {cargoApplications.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No cargo liner parts linked.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {cargoApplications.map((app) => (
                              <tr key={app.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2 w-32">
                                  <button
                                    onClick={() => { setEditCargoPartId(app.PartId); setEditCargoPartNumber(app.PartNumber); }}
                                    className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                                  >
                                    {app.PartNumber}
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {app.Extras ?? ''}
                                </td>
                                <td className="px-3 py-2 text-right w-16">
                                  <button
                                    onClick={() => handleRemoveCargoApplication(app.Id)}
                                    className="text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Dash Cover Applications (edit mode only) */}
                {editingVehicle && partType === 'Dash Cover' && (
                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">Dash Cover Applications</h4>
                    </div>
                    <hr className="border-gray-200 dark:border-gray-700 mb-3" />

                    <div className="flex justify-end mb-3">
                      <button
                        onClick={() => setShowAddDashModal(true)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        + Add Part
                      </button>
                    </div>

                    {dashApplications.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No dash cover parts linked.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {dashApplications.map((app) => (
                              <tr key={app.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2 w-32">
                                  <button
                                    onClick={() => { setEditDashPartId(app.PartId); setEditDashPartNumber(app.PartNumber); }}
                                    className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                                  >
                                    {app.PartNumber}
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {app.Extras ?? ''}
                                </td>
                                <td className="px-3 py-2 text-right w-16">
                                  <button
                                    onClick={() => handleRemoveDashApplication(app.Id)}
                                    className="text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="-mx-6 mt-6 px-6 py-4 bg-gray-100 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-600 flex gap-2">
                  <button
                    onClick={editingVehicle ? handleEditVehicleSubmit : handleAddVehicleSubmit}
                    className="px-5 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                  >
                    {editingVehicle ? 'Save Changes' : 'Submit'}
                  </button>
                  <button
                    onClick={() => { setEditingVehicle(null); setView('index'); }}
                    className="px-5 py-1.5 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-50 dark:hover:bg-gray-500 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      {editAppPartId !== null && (
        <EditApplicationModal
          partId={editAppPartId}
          partNumber={editAppPartNumber}
          onClose={() => setEditAppPartId(null)}
          onSaved={async () => {
            setEditAppPartId(null);
            if (editingVehicle) {
              const apps = await vehicleService.getApplications(editingVehicle.Id!);
              setVehicleApplications(Array.isArray(apps) ? apps : []);
            }
          }}
        />
      )}
      {showAddAppModal && editingVehicle && (
        <AddApplicationModal
          vehicleId={editingVehicle.Id!}
          onClose={() => setShowAddAppModal(false)}
          onSaved={async () => {
            setShowAddAppModal(false);
            const apps = await vehicleService.getApplications(editingVehicle.Id!);
            setVehicleApplications(Array.isArray(apps) ? apps : []);
          }}
        />
      )}
      {showAddCargoModal && editingVehicle && (
        <AddCargoApplicationModal
          vehicleId={editingVehicle.Id!}
          onClose={() => setShowAddCargoModal(false)}
          onSaved={async () => {
            setShowAddCargoModal(false);
            const cargo = await vehicleService.getCargoApplications(editingVehicle.Id!);
            setCargoApplications(Array.isArray(cargo) ? cargo : []);
          }}
        />
      )}
      {editCargoPartId !== null && (
        <EditCargoApplicationModal
          partId={editCargoPartId}
          partNumber={editCargoPartNumber}
          onClose={() => setEditCargoPartId(null)}
          onSaved={async () => {
            setEditCargoPartId(null);
            if (editingVehicle) {
              const cargo = await vehicleService.getCargoApplications(editingVehicle.Id!);
              setCargoApplications(Array.isArray(cargo) ? cargo : []);
            }
          }}
        />
      )}
      {showAddDashModal && editingVehicle && (
        <AddDashApplicationModal
          vehicleId={editingVehicle.Id!}
          onClose={() => setShowAddDashModal(false)}
          onSaved={async () => {
            setShowAddDashModal(false);
            const dash = await vehicleService.getDashApplications(editingVehicle.Id!);
            setDashApplications(Array.isArray(dash) ? dash : []);
          }}
        />
      )}
      {editDashPartId !== null && (
        <EditDashApplicationModal
          partId={editDashPartId}
          partNumber={editDashPartNumber}
          onClose={() => setEditDashPartId(null)}
          onSaved={async () => {
            setEditDashPartId(null);
            if (editingVehicle) {
              const dash = await vehicleService.getDashApplications(editingVehicle.Id!);
              setDashApplications(Array.isArray(dash) ? dash : []);
            }
          }}
        />
      )}
    </MainLayout>
  );
};

export default Vehicles;
